// Mirror of reilm-be/modules/properties/flipAnalysisFields.js FLIP_ANALYSIS_NUMBER_FIELDS -- the
// scenario leaves the backend validator requires to be `Number.isFinite` or null. Kept in sync by
// scenarioNumbers.test.js; see README.md ("scenario fields hold numbers, not typed text").
export const SCENARIO_NUMBER_FIELDS = new Set([
  'purchase_price',
  'rehab_costs',
  'overbudget',
  'arv',
  'holding_period',
  'br4_loan_cost1',
  'br4_loan_cost2',
  'br4_ltv1',
  'br4_ltv2',
  'inspection_costs',
  'earnest_money',
  'agent_commission1',
  'agent_commission2',
  'insurance_policy1',
  'insurance_policy2',
  'title_fees1',
  'title_fees2',
  'buy_property_tax1',
  'buy_seller_credits',
  'buy_down_payment',
  'financed_draw_fee',
  'utilities0',
  'utilities1',
  'holding_hoa_fee1',
  'holding_hoa_fee2',
  'marketing2',
  'selling_commission0',
  'selling_commission1',
  'concessions_to_buyer1',
  'concessions_to_buyer2',
  'sell_property_tax0',
  'sell_other2',
  'rehab_financed',
  'financed_down_payment1',
  'financed_down_payment2',
  'interest_rate',
  'origination_points0',
  'origination_points1',
  'service_fees',
  'drawn_funds',
  'tax_out_of_pocket',
])

// Typed text -> the value the scenario stores. Blank or unparseable -> null, the validator's own
// "no value" (never 0, which would read as a real entered zero -- verification-runs F4).
// Thousands separators, "$" and "%" are display characters, not part of the number.
export function parseScenarioNumber(raw) {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  if (raw == null) return null
  const cleaned = String(raw).replace(/[\s,$%]/g, '')
  // Plain decimal only: Number() also accepts '0x10', '0b11', '0o7' and '1e3'.
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(cleaned)) return null
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}

// The shared validator's bound on every numeric scenario leaf and schedule slot (reilm-be
// FLIP_MAX_ABS_NUMBER): past it is a 400 on every compute, so the value is held at the edge instead.
export const SCENARIO_NUMBER_MAX_ABS = 1e12

function clampAbs(n) {
  if (n == null) return n
  return Math.min(SCENARIO_NUMBER_MAX_ABS, Math.max(-SCENARIO_NUMBER_MAX_ABS, n))
}

// paySchedProrations slots: the validator accepts finite numbers only (no null), and 0 is the
// formula core's own "no override" (interestSchedule.js), so a cleared slot goes back to 0.
export function parseScheduleSlot(raw) {
  return clampAbs(parseScenarioNumber(raw) ?? 0)
}

// The compute route's own bound (reilm-be FLIP_MAX_HOLDING_PERIOD): above it is a 400, so the
// input stops at it instead of turning every later keystroke into an error.
export const HOLDING_PERIOD_MAX = 600

// reilm-be FLIP_MAX_PAY_SCHED_LENGTH (600 + 1): the validator refuses a longer paySchedProrations.
export const PAY_SCHED_MAX_SLOTS = HOLDING_PERIOD_MAX + 1

// A typed or loaded value for `field`, parsed and held to the bounds the backend enforces.
export function parseScenarioField(field, raw) {
  const n = clampAbs(parseScenarioNumber(raw))
  if (field === 'holding_period' && n != null && n > HOLDING_PERIOD_MAX) return HOLDING_PERIOD_MAX
  return n
}

// Same rule for a whole scenario arriving from outside the inputs (a loaded property, an imported
// template): numeric leaves are coerced, every other field is returned untouched.
export function normalizeScenarioNumbers(scenario) {
  const next = { ...scenario }
  for (const key of Object.keys(next)) {
    if (SCENARIO_NUMBER_FIELDS.has(key)) next[key] = parseScenarioField(key, next[key])
  }
  if (Array.isArray(next.paySchedProrations)) next.paySchedProrations = next.paySchedProrations.map(parseScheduleSlot)
  return next
}
